'use client';

import {
  BedDouble,
  Building2,
  FileText,
  Handshake,
  HeartPulse,
  Hospital,
  Mail,
  MapPin,
  Phone,
  Pill,
  Stethoscope,
  Store,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AccountUserType } from '../../schema/professional-profile.schema';

/**
 * Profile data for display in settings.
 * `data` contains professional profile info stored in accounts.profile_data.
 */
interface ProfileData {
  userType: AccountUserType | null;
  /** Professional profile data (name, license_number, phone_number, address) */
  data: Record<string, string | undefined> | null;
  verified: boolean;
}

interface ProfileSectionProps {
  profile: ProfileData;
  email?: string;
}

const userTypeLabels: Record<AccountUserType, string> = {
  doctor: 'Lekarz',
  pharmacist: 'Farmaceuta',
  nurse_midwife: 'Pielęgniarka / Położna',
  pharma: 'Apteka',
  medical_place: 'Placówka medyczna',
  hospital: 'Szpital',
  partner: 'Partner',
};

const userTypeIcons: Record<AccountUserType, React.ReactNode> = {
  doctor: <Stethoscope className="h-5 w-5" strokeWidth={1} />,
  pharmacist: <Pill className="h-5 w-5" strokeWidth={1} />,
  nurse_midwife: <HeartPulse className="h-5 w-5" strokeWidth={1} />,
  pharma: <Store className="h-5 w-5" strokeWidth={1} />,
  medical_place: <Hospital className="h-5 w-5" strokeWidth={1} />,
  hospital: <BedDouble className="h-5 w-5" strokeWidth={1} />,
  partner: <Handshake className="h-5 w-5" strokeWidth={1} />,
};

export function ProfileSection({ profile, email }: ProfileSectionProps) {
  const { t } = useTranslation('account');

  if (!profile.userType) {
    return (
      <div className="text-muted-foreground text-sm">
        Profil zawodowy nie został jeszcze uzupełniony.
      </div>
    );
  }

  const isIndividual =
    profile.userType === 'doctor' ||
    profile.userType === 'pharmacist' ||
    profile.userType === 'nurse_midwife';

  return (
    <div className="space-y-4">
      {/* User type badge */}
      <div className="flex items-center gap-3">
        <div className="bg-background border-border/60 flex h-10 w-10 items-center justify-center rounded-full border">
          <span className="text-muted-foreground">
            {userTypeIcons[profile.userType]}
          </span>
        </div>
        <div>
          <p className="font-medium">{userTypeLabels[profile.userType]}</p>
        </div>
      </div>

      {/* Email */}
      {email && (
        <ProfileField
          icon={<Mail className="h-3.5 w-3.5" />}
          label={t('profileWizard.email')}
          value={email}
        />
      )}

      {/* Profile details */}
      <div className="space-y-4">
        {isIndividual ? (
          <>
            <ProfileField
              icon={<User className="h-3.5 w-3.5" />}
              label={t('profileWizard.fullName')}
              value={profile.data?.name}
            />
            <ProfileField
              icon={<FileText className="h-3.5 w-3.5" />}
              label={t('profileWizard.licenseNumber')}
              value={profile.data?.license_number}
            />
          </>
        ) : (
          <>
            <ProfileField
              icon={<Building2 className="h-3.5 w-3.5" />}
              label={t('profileWizard.organizationName')}
              value={profile.data?.name}
            />
            <ProfileField
              icon={<Phone className="h-3.5 w-3.5" />}
              label={t('profileWizard.phoneNumber')}
              value={profile.data?.phone_number}
            />
            {profile.data?.address && (
              <ProfileField
                icon={<MapPin className="h-3.5 w-3.5" />}
                label={t('profileWizard.address')}
                value={profile.data.address}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProfileField({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) {
  if (!value) return null;

  return (
    <div className="flex items-start gap-3">
      <div className="text-muted-foreground mt-1">{icon}</div>
      <div>
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}
